import 'reflect-metadata';
import 'core-js/stable';
import 'regenerator-runtime/runtime';
import React from 'react';
import { render } from 'react-dom';
import { ThemeProvider } from 'styled-components';
import i18next from 'i18next';
import { Provider } from 'inversify-react';
import Layout from './components/Layout';
import MESSAGE_TYPES from '../constants/MESSAGE_TYPES';
import { isMobile } from '../helpers/device';
import initI18n from '../lib/i18n';
import { AppSettings } from '../types/settings';
import ErrorMessage from './components/ErrorMessage';
import { container } from '../lib/widget_announcements';
import type { Widget } from '../types/widget';
import { WIDGET } from '../constants/WIDGET';
import AppSkeleton from '../shared/components/AppSkeleton';

class App extends AppSkeleton {
  constructor(props: AppSettings) {
    super(props);

    this.render(props);
  }

  async render({ node, uuid, previewMode = false, event, settings = {}, flags }: AppSettings) {
    const { buttonColor, lang, buttonBackground, backgroundColor, backgroundImage } = settings;

    initI18n({ lng: lang });

    await i18next.loadNamespaces(['common']);
    const isRtl = ['ar', 'he'].includes(lang);
    const renderNode = document.getElementById(node);
    const theme = {
      isMobile: isMobile(),
      isRtl,
      buttonColor,
      buttonBackground,
      backgroundColor,
      backgroundImage,
      withAnnouncements: settings.withAnnouncements,
      isLanding: settings.isLanding,
    };

    try {
      const widget: Widget = container.get(WIDGET);
      window.Widget = widget;
      widget.setSettings({
        uuid,
        settings,
      });
      widget.setFlags && widget.setFlags(flags);

      const { pricesDates, selectedEvent } = settings;

      if (settings.withAnnouncements && pricesDates) {
        const price = pricesDates.find((p) => p.id === selectedEvent);
        const hasAnnouncements = price && price.use_announcements;
        if (hasAnnouncements) {
          await widget.loadAnnouncements();
          await widget.loadStandingTickets();
        }
      }

      render(
        <ThemeProvider theme={theme}>
          <Provider container={container}>
            <Layout uuid={uuid} event={event} previewMode={previewMode} settings={settings} />
          </Provider>
        </ThemeProvider>,
        renderNode,
      );

      widget.loadData();

      window.addEventListener('message', this.handleReceiveMessage, false);
    } catch (e) {
      console.log(e);
      render(
        <Provider container={container}>
          <ThemeProvider theme={theme}>
            <ErrorMessage />
          </ThemeProvider>
        </Provider>,
        renderNode,
      );
    }
  }

  handleReceiveMessage = (event) => {
    const message = event.message ? 'message' : 'data';
    const { type } = event[message];

    switch (type) {
      case MESSAGE_TYPES.UNSELECT_TICKETS:
        window.Widget.announcementsController.unselect();
        window.Widget.ticketsController.unselect();
        break;

      case MESSAGE_TYPES.UNSELECT_TICKET:
        window.Widget.ticketsController.unselectTicketsIds(event[message].tickets);
        break;

      case MESSAGE_TYPES.DELETE_MARKETPLACE_TICKET:
        window.Widget.ticketsController.deleteMarketplaceTicket(event[message].id);
        window.Widget.standingTicketsController.deleteTicket(event[message].id);
        break;

      case MESSAGE_TYPES.UPDATE_MARKETPLACE_TICKET:
        const { ticketData } = event[message];
        if (event[message].ticketData.isCategoryTicket) {
          const { id, newCount } = ticketData;
          window.Widget.categoriesController.addSilency(id, newCount);
        } else {
          window.Widget.ticketsController.updateMarketplaceTicket(ticketData);
        }

        window.Widget.ticketsController.emitUpdpateTickets(ticketData);
        break;

      case MESSAGE_TYPES.SET_SELECTED_DATE:
        window.Widget.metaController.setSelectDate(event[message].date);
        break;

      case MESSAGE_TYPES.SET_SELECTED_TICKETS:
        window.Widget.categoriesController.setSelectedCategories(event[message].tickets);
        window.Widget.ticketsController.setSelectedTickets(event[message].tickets);
        break;

      default:
        break;
    }
  };
}

if (module.hot) {
  module.hot.accept();
}

declare global {
  interface Window {
    App: any;
    Widget: any;
  }
}

window.App = App;
